import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import config from '../config/config';
import { isAdmin as permIsAdmin, isAccounting as permIsAccounting } from '../utils/permissions';

const API_BASE = config.api.baseUrl;

// Create context
const PermissionsContext = createContext(null);

// Default permissions for system_admin (full access)
const FULL_ACCESS = {
  view: true,
  create: true,
  edit: true,
  delete: true,
  buttons: {} // All buttons enabled by default for admin
};

/**
 * PermissionsProvider - Provides user permissions throughout the app
 */
export const PermissionsProvider = ({ children }) => {
  const [permissions, setPermissions] = useState({});
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isFullAccess, setIsFullAccess] = useState(false); // Full access to objects (admin + accounting) but not necessarily Setup

  // Load permissions when user logs in.
  //
  // PR-F (Phase 3 of user/profile audit, 2026-05-29): the first-arg used
  // to be `profile` (bare string). It's now `userOrProfile` — accepts
  // either the full user object OR a bare profile string for backward
  // compat. Passing the full user lets isAdmin() also honour the
  // standalone is_admin/isAdmin boolean flag (audit found those
  // sometimes disagree with profile).
  const loadPermissions = useCallback(async (userOrProfile, username) => {
    // Normalise — extract profile string + full user object.
    let profile;
    let userObj;
    if (typeof userOrProfile === 'string' || userOrProfile == null) {
      profile = userOrProfile || '';
      userObj = profile ? { profile } : {};
    } else {
      userObj = userOrProfile || {};
      profile = userObj.profile || userObj.Profile || '';
    }

    if (!profile) {
      setPermissions({});
      setUserProfile(null);
      setIsAdmin(false);
      setIsFullAccess(false);
      setLoading(false);
      return;
    }

    setUserProfile(profile);

    // System admin has full access (including Setup). The old check was
    //   profile === 'system_admin' || 'מנהל מערכת' || 'Admin'
    // permIsAdmin() canonicalises legacy variants AND honours the
    // standalone isAdmin/is_admin flag — net effect is at least as
    // permissive as before, never less.
    if (permIsAdmin(userObj)) {
      setIsAdmin(true);
      setIsFullAccess(true);
      setPermissions({});
      setLoading(false);
      return;
    }

    // Accounting profile — full data access, NOT admin (no Setup access).
    // The old check was profile === 'accounting' || 'הנהלת חשבונות'.
    // permIsAccounting() canonicalises legacy variants; admin is a
    // superset of accounting but the isAdmin branch above already
    // short-circuited for admins.
    if (permIsAccounting(userObj)) {
      setIsAdmin(false);
      setIsFullAccess(true);
      setPermissions({});
      setLoading(false);
      return;
    }

    setIsAdmin(false);
    setIsFullAccess(false);

    let profilePerms = {};
    try {
      const res = await fetch(`${API_BASE}/api/profiles/${profile}/permissions`);
      if (res.ok) {
        const data = await res.json();
        profilePerms = data.permissions || {};
      }
    } catch (err) {
      console.error('Error loading profile permissions:', err);
    }

    // Check for user-specific permissions that override profile permissions
    if (username) {
      try {
        const res = await fetch(`${API_BASE}/api/users/${encodeURIComponent(username)}/permissions`);
        if (res.ok) {
          const data = await res.json();
          if (data.permissions && typeof data.permissions === 'object' && Object.keys(data.permissions).length > 0) {
            // User has custom permissions - use them instead of profile
            setPermissions(data.permissions);
            setLoading(false);
            return;
          }
        }
      } catch (err) {
        console.error('Error loading user permissions:', err);
      }
    }

    // Fall back to profile permissions
    setPermissions(profilePerms);
    setLoading(false);
  }, []);

  // Check if user can view an object
  const canView = useCallback((objectName) => {
    if (isAdmin || isFullAccess) return true;
    return permissions[objectName]?.view !== false;
  }, [isAdmin, isFullAccess, permissions]);

  // Check if user can create records for an object
  const canCreate = useCallback((objectName) => {
    if (isAdmin || isFullAccess) return true;
    return permissions[objectName]?.create === true;
  }, [isAdmin, isFullAccess, permissions]);

  // Check if user can edit records for an object
  const canEdit = useCallback((objectName) => {
    if (isAdmin || isFullAccess) return true;
    return permissions[objectName]?.edit === true;
  }, [isAdmin, isFullAccess, permissions]);

  // Check if user can delete records for an object
  const canDelete = useCallback((objectName) => {
    if (isAdmin || isFullAccess) return true;
    return permissions[objectName]?.delete === true;
  }, [isAdmin, isFullAccess, permissions]);

  // Check if a button should be visible for an object
  const isButtonVisible = useCallback((objectName, buttonId) => {
    if (isAdmin || isFullAccess) return true;

    const objPerms = permissions[objectName];
    if (!objPerms || !objPerms.buttons) return true; // Default to visible if not configured

    // If button is explicitly set, use that value
    if (buttonId in objPerms.buttons) {
      return objPerms.buttons[buttonId];
    }

    // Default to visible
    return true;
  }, [isAdmin, permissions]);

  // Get all permissions for an object
  const getObjectPermissions = useCallback((objectName) => {
    if (isAdmin || isFullAccess) {
      return { ...FULL_ACCESS };
    }
    return permissions[objectName] || { view: true, create: false, edit: false, delete: false, buttons: {} };
  }, [isAdmin, isFullAccess, permissions]);

  // Filter buttons array based on permissions
  const filterButtons = useCallback((objectName, buttons) => {
    if (isAdmin || isFullAccess) return buttons;

    return buttons.filter(btn => {
      const buttonId = btn.id || btn.action;

      // Check specific button permission
      if (!isButtonVisible(objectName, buttonId)) {
        return false;
      }

      // Also check action-based permissions
      if (buttonId === 'edit' && !canEdit(objectName)) return false;
      if (buttonId === 'delete' && !canDelete(objectName)) return false;
      if (buttonId === 'clone' && !canCreate(objectName)) return false;

      return true;
    });
  }, [isAdmin, isFullAccess, isButtonVisible, canEdit, canDelete, canCreate]);

  // Load permissions on mount (check for existing session)
  useEffect(() => {
    const loadExistingSession = async () => {
      try {
        const userStr = localStorage.getItem('salesforce_user') || sessionStorage.getItem('salesforce_user');
        if (userStr) {
          const user = JSON.parse(userStr);
          // PR-F: pass the full user object so loadPermissions can also
          // honour the standalone is_admin/isAdmin flag (audit finding).
          // Apply the existing 'service_center' default if profile is
          // missing — preserved for backwards compatibility.
          const userForPerms = (user.profile || user.Profile)
            ? user
            : { ...user, profile: 'service_center' };
          await loadPermissions(userForPerms, user.username || user.Username);
        } else {
          setLoading(false);
        }
      } catch (err) {
        console.error('Error loading session:', err);
        setLoading(false);
      }
    };

    loadExistingSession();
  }, [loadPermissions]);

  const value = {
    permissions,
    userProfile,
    isAdmin,
    isFullAccess,
    loading,
    loadPermissions,
    canView,
    canCreate,
    canEdit,
    canDelete,
    isButtonVisible,
    getObjectPermissions,
    filterButtons,
  };

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
};

/**
 * Hook to use permissions
 */
export const usePermissions = () => {
  const context = useContext(PermissionsContext);
  if (!context) {
    // Return default values if not wrapped in provider
    return {
      permissions: {},
      userProfile: null,
      isAdmin: true, // Default to admin if no context
      loading: false,
      loadPermissions: () => {},
      canView: () => true,
      canCreate: () => true,
      canEdit: () => true,
      canDelete: () => true,
      isButtonVisible: () => true,
      getObjectPermissions: () => ({ view: true, create: true, edit: true, delete: true, buttons: {} }),
      filterButtons: (_, buttons) => buttons,
    };
  }
  return context;
};

export default PermissionsContext;
